import {useCallback, useEffect, useRef, useState} from "react";
import {observer} from "mobx-react";
import {useServices} from "../../../react-hooks/use-services.hook";
import {FlexContainerBox} from "../../../themes/architecture/css-helpers/flex";
import {CongratsTextBox} from "./boxes/congrats-text.box";
import {ConicShadowBox} from "./boxes/conic-shadow.box";
import {GradientBorderBox} from "./boxes/gradient-border.box";
import {AvailableTicketCountBadgeBox} from "./boxes/available-ticket-count-badge.box";
import {ProgressPieBox} from "./boxes/progress-pie.box";
import {ProgressTextBox} from "./boxes/progress-text.box";
import {WrapperPositionAlignmentBox} from "./boxes/wrapper-position-alignment.box";
import {WrapperPositionContextBox} from "./boxes/wrapper-position-context.box";
import {CyClass} from "../../../../common/cy-classes";
import classNames from "classnames";
import {IGC_DEFAULT_TOOLTIP_TIMEOUT} from "../../../../common/const/in-game-challenge.consts";
import {NEW_TICKET_NOTIFICATION} from "../../../services/magic-8-ball/new-ticket-notification-strategy.enum";
import {usePrevious} from "../../../react-hooks/use-previous.hook";

interface IJackpotBarMagicBallComponentProps {
    verticalMode?: boolean;
}

export const JackpotBarMagicBallComponent: React.FC<IJackpotBarMagicBallComponentProps> = observer((props) => {
    const services = useServices();
    const buttonWrapperRef = useRef<HTMLDivElement | null>(null);
    const prevAvailableTicketCount = useRef<number | null>(null);
    const bigSizeTimeoutRef = useRef<number | null>(null);
    const [bigSize, setBigSize] = useState<boolean>(false);

    const percentage = services.inGameChallenge.currentProgressPercentage;
    // we need to remember the previous value to compare it with the current one. if the current one is smaller, we don't want to make it look like the progress is decreasing so we first reset the progress bar
    // it happens when the user reach 100% of the challenge and starts a new challenge
    const previousPercentage = usePrevious(percentage);
    const [shownPercentage, setShownPercentage] = useState(percentage);
    const [animationProgressDisabled, setAnimationProgressDisabled] = useState(false);
    const availableTicketCount = services.inGameTicket.availableTickets.length;
    const allActiveCampaignRulesCompleted = services.magic8ball.allActiveCampaignRulesCompleted;

    const _sendAnalytics = useCallback((action: string, label: string) => {
        services.inGameChallenge.sendAnalytics(action, label);
    }, [services.inGameChallenge]);

    useEffect(() => {
        // This effect runs only once, on the first render
        _sendAnalytics('Show', 'Magic8BallIconInGame');
    }, [_sendAnalytics]);

    useEffect(() => {
        if (buttonWrapperRef?.current) {
            services.magic8ball.setMagic8BallWrapper(buttonWrapperRef.current);
            services.magic8ball.showInitialTooltip();
        } else {
            services.magic8ball.setMagic8BallWrapper(null);
        }
    }, [services.magic8ball]);

    useEffect(() => {
        if (prevAvailableTicketCount.current !== null && prevAvailableTicketCount.current < availableTicketCount) {
            if (services.magic8ball.newTicketNotificationStrategy === NEW_TICKET_NOTIFICATION.BALL_PUMP) {
                setBigSize(true);
                bigSizeTimeoutRef.current = window.setTimeout(() => {
                    setBigSize(false);
                    bigSizeTimeoutRef.current = null;
                }, IGC_DEFAULT_TOOLTIP_TIMEOUT);
                prevAvailableTicketCount.current = availableTicketCount;
                _sendAnalytics('Show', 'IGCNewTicketNotification');
            } else {
                services.magic8ball.showCampaignEndDateTooltip();
            }
        } 
        prevAvailableTicketCount.current = availableTicketCount;

        return () => {
            window.clearTimeout(bigSizeTimeoutRef?.current);
            bigSizeTimeoutRef.current = null;
        }
    }, [prevAvailableTicketCount, availableTicketCount, services.magic8ball, _sendAnalytics]);

    useEffect(() => {
        // When starting a new challenge, the percentage goes from 100% to a lower value.
        // In order not to seem like the circle around the 8 ball decreases, we take the following steps
        if (previousPercentage > percentage) {
            // disable the yellow circle progress animation
            setAnimationProgressDisabled(true);
            // reset the yellow circle progress to 0
            setShownPercentage(0);
            // wait for rerender
            requestAnimationFrame(() => {
                // enable the animation again
                setAnimationProgressDisabled(false);
                // set the percentage and the animation will appear like it goes from 0 to percentage
                setShownPercentage(percentage);
            })
        } else {
            // set percentage
            setShownPercentage(percentage);
        }
    }, [previousPercentage, percentage]);

    const onMagic8BallClick = () => {
        if (allActiveCampaignRulesCompleted && availableTicketCount === 0) {
            return;
        }
        services.magic8ball.showMagicBall();
    }

    const gradientColors = [
        services.theme.currentTheme.colors.brandSecondary,
        "#4024FF"
    ]
    const conicGradient = `conic-gradient(from var(--deg), ${gradientColors[0]}, ${gradientColors[0]}, ${gradientColors[0]}, ${gradientColors[1]}, ${gradientColors[1]}, ${gradientColors[1]}, ${gradientColors[0]}, ${gradientColors[0]}, ${gradientColors[0]}, ${gradientColors[1]}, ${gradientColors[1]}, ${gradientColors[1]}, ${gradientColors[0]})`;

    /**
     * Normally instead of this thing I should use:
     * <img
     *     src={services.theme.currentTheme.assets.images.magic_8_ball.button.magic_8_ball_button_icon_png.toString()}
     *     srcSet={`${services.theme.currentTheme.assets.images.magic_8_ball.button.magic_8_ball_button_icon_png.toString()} 1x, ${services.theme.currentTheme.assets.images.magic_8_ball.button.magic_8_ball_button_icon_2x_png.toString()} 2x`}
     * />
     * But this source image is used inside svg's <image> tag and this does not support srcSet.
     * The reason I am not extracting <image> from svg to <img> in html is that the rendered image is 21px and
     * in html I get an alignment offset, is not perfect centered
     */
    const pixelRatio = services.device.viewport.pixelRatio;
    let ballImgSrc = services.theme.currentTheme.assets.images.magic_8_ball.button.magic_8_ball_button_icon_png.toString();
    if (pixelRatio > 1 && pixelRatio < 2) {
        ballImgSrc = services.theme.currentTheme.assets.images.magic_8_ball.button.magic_8_ball_button_icon_15x_png.toString();
    } else if (pixelRatio >= 2 && pixelRatio < 3) {
        ballImgSrc = services.theme.currentTheme.assets.images.magic_8_ball.button.magic_8_ball_button_icon_2x_png.toString();
    } else if (pixelRatio >= 3) {
        ballImgSrc = services.theme.currentTheme.assets.images.magic_8_ball.button.magic_8_ball_button_icon_3x_png.toString();
    }

    return <WrapperPositionContextBox verticalMode={props.verticalMode}>
        <WrapperPositionAlignmentBox
            ref={buttonWrapperRef}
            className={CyClass('cy-magic8ball-wrapper')}
            onClick={() => _sendAnalytics('Click', 'Magic8BallIconInGame')}
        >
            <ConicShadowBox
                show={bigSize}
                background={conicGradient}
            />
            <GradientBorderBox
                onClick={onMagic8BallClick}
                isClickable={!allActiveCampaignRulesCompleted || availableTicketCount > 0}
                borderRadius="50px"
                borderWidth="1px"
                borderGradient={conicGradient}
                rotation={bigSize}
                className={classNames(CyClass('cy-magic8ball-button'))}
            >
                <FlexContainerBox alignItems="center">
                    <FlexContainerBox alignItems="center" direction={props.verticalMode ? "column" : "row"}>
                        <ProgressPieBox bigSize={bigSize} animationProgressDisabled={animationProgressDisabled}>
                            <svg viewBox="0 0 30 30">
                                <circle
                                    id="grey_outline"
                                    r="15" cx="15" cy="15"
                                ></circle>
                                <circle
                                    id="yellow_progress"
                                    r="14" cx="15" cy="15" strokeLinecap="round"
                                    strokeDashoffset={0}
                                    strokeWidth={3}
                                    strokeDasharray={shownPercentage * 2 * Math.PI * 14 / 100 + ", " + (2 * Math.PI * 14)}
                                ></circle>
                                <circle
                                    id="blue_inline"
                                    r="12" cx="15" cy="15" strokeLinecap="round"
                                ></circle>
                                <image id="ball_image" x="4.5" y="4.5" width="21" height="21" href={ballImgSrc} />
                            </svg>
                        </ProgressPieBox>
                        <ProgressTextBox bigSize={bigSize} verticalMode={props.verticalMode} className={CyClass('cy-magic8ball-progress-text')}>
                            {allActiveCampaignRulesCompleted ? services.translation.translate("MAGIC_8_BALL_ALL_RULES_COMPLETED_CONGRATS") : `${percentage}%`}
                        </ProgressTextBox>
                    </FlexContainerBox>
                    <CongratsTextBox bigSize={bigSize}>
                        {services.translation.translate("MAGIC_8_BALL_GRANTED_TICKET")}
                    </CongratsTextBox>
                </FlexContainerBox>
                {availableTicketCount > 0 ? <AvailableTicketCountBadgeBox
                    bigSize={bigSize}
                    className={CyClass('cy-magic8ball-number-of-shakes-badge')}
                >
                    {availableTicketCount}
                </AvailableTicketCountBadgeBox> : null}
            </GradientBorderBox>
        </WrapperPositionAlignmentBox>
    </WrapperPositionContextBox>;
});
